import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { vars } from '@etg/wings';
import Media from 'react-media';
import { usePrevious } from '@eti/utils';
import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import TravelerDescription from '../../../common/components/TravelerDescription';
import { TRACKING_VALUES, useProductSnowplowTracking } from '../../../custom-hooks/useProductSnowplowTracking';
import { useDidUpdateEffect } from '../../../custom-hooks/useDidUpdateEffect';
import ManulifeTravelerDateOfBirth from './ManulifeTravelerDateOfBirth';
import ManulifeSwitchButton from './ManulifeSwitchButton';
import ManulifeTravelerPrice, { noPriceValue } from './ManulifeTravelerPrice';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const itemStyles = isSelected => css`
  background-color: ${isSelected ? 'inherit' : etiColors.grayScale[2]};
  border-bottom: 1px solid ${etiColors.grayScale[6]};
  padding: 15px;
  transition: 0.3s;

  &:first-child {
    border-top: 1px solid ${etiColors.grayScale[6]};
  }

  &:last-child {
    border-bottom: none;
    border-bottom-left-radius: ${vars.card.borderRadius};
    border-bottom-right-radius: ${vars.card.borderRadius};
  }
`;
const itemContentStyles = css`
  @media ${mediaQueries.medium.up} {
    align-items: flex-start;
    display: flex;
    justify-content: space-between;
  }
`;
const inputWrapperStyles = css`
  display: flex;
  flex-direction: column;
`;
const desktopPriceWrapperStyles = css`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  padding-bottom: 20px;
`;
const dateOfBirthWrapperStyles = css`
  display: flex;
  justify-content: space-between;
`;
const travelerDescription = css`
  align-items: center;
  color: ${etiColors.black};
  display: flex;
`;
const travelerPriceDesktopStyles = noAvailablePrice => css`
  margin-top: ${noAvailablePrice ? '26px' : '10px'};
`;
const ManulifeTraveler = _ref => {
  let {
    addTravelerPrice,
    addTravelerPriceError,
    ageType,
    dateFormat,
    dateOfBirth,
    isActive,
    isDateOfBirthReadOnly,
    id,
    isFetchingPrice,
    isRefetchingPrice,
    isSelected,
    isValidDateOfBirth,
    name,
    onFetchPrice,
    price,
    productName,
    province,
    selectTraveler,
    setTravelerFetchingPrice,
    setTravelerRefetchingPrice
  } = _ref;
  const [isFetchPriceError, setFetchPriceError] = useState(false);
  const {
    sendAddToCartEvent,
    sendSelectEvent
  } = useProductSnowplowTracking(productName);
  const hasDateOfBirth = dateOfBirth && isValidDateOfBirth;
  const provinceValue = province === null || province === void 0 ? void 0 : province.value;
  const previousProvinceValue = usePrevious(provinceValue);
  const hasNewProvince = provinceValue && provinceValue !== previousProvinceValue;
  const disabled = Boolean(!hasDateOfBirth || isFetchPriceError || isFetchingPrice || price === null);
  const fetchPrice = useCallback(async () => {
    setTravelerFetchingPrice(true);
    try {
      const priceRaw = await onFetchPrice(province, dateOfBirth, id);
      selectTraveler(priceRaw !== null);
      addTravelerPrice(priceRaw);
      addTravelerPriceError(null);
      setFetchPriceError(false);
      if (priceRaw !== null) {
        sendAddToCartEvent({
          value: TRACKING_VALUES.PROVINCE_SELECT
        });
      } else {
        sendAddToCartEvent({
          addToCart: false,
          value: `Province unavailable: ${province === null || province === void 0 ? void 0 : province.label}`
        });
      }
    } catch (error) {
      console.error(error); // eslint-disable-line no-console
      selectTraveler(false);
      setFetchPriceError(error);
      addTravelerPriceError(error);
    }
    setTravelerFetchingPrice(false);
  }, [addTravelerPrice, addTravelerPriceError, dateOfBirth, id, onFetchPrice, province, selectTraveler, sendAddToCartEvent, setTravelerFetchingPrice]);
  const handleFetchPrice = () => {
    if (hasDateOfBirth) {
      fetchPrice();
    } else {
      selectTraveler(false);
      addTravelerPrice(noPriceValue);
      addTravelerPriceError(null);
      setFetchPriceError(false);
    }
  };
  useEffect(() => {
    if (hasDateOfBirth && isRefetchingPrice && provinceValue) {
      setTravelerRefetchingPrice(false);
      fetchPrice();
    }
  }, [fetchPrice, hasDateOfBirth, isRefetchingPrice, provinceValue, setTravelerRefetchingPrice]);
  useEffect(() => {
    if (hasDateOfBirth && hasNewProvince) {
      fetchPrice();
    }
  }, [fetchPrice, hasDateOfBirth, hasNewProvince]);
  useEffect(() => {
    if (isDateOfBirthReadOnly && !hasDateOfBirth) {
      sendAddToCartEvent({
        addToCart: false,
        value: `Error province unavailable: ${province === null || province === void 0 ? void 0 : province.label}`
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provinceValue]);
  useDidUpdateEffect(() => {
    if (isActive && Boolean(province)) {
      if (isSelected) {
        sendAddToCartEvent({
          value: TRACKING_VALUES.RADIO_BUTTON
        });
      } else {
        sendSelectEvent();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);
  return /*#__PURE__*/_jsx("li", {
    className: itemStyles(isSelected),
    "data-testid": `${productName}-traveler-${id}`,
    children: /*#__PURE__*/_jsxs("div", {
      className: itemContentStyles,
      children: [/*#__PURE__*/_jsxs("div", {
        className: inputWrapperStyles,
        children: [/*#__PURE__*/_jsx(TravelerDescription, {
          className: cx('manulife', travelerDescription),
          name: name,
          shouldShowIcon: true,
          type: ageType
        }), /*#__PURE__*/_jsxs("div", {
          className: dateOfBirthWrapperStyles,
          children: [/*#__PURE__*/_jsx(ManulifeTravelerDateOfBirth, {
            ageType: ageType,
            dateFormat: dateFormat,
            id: id,
            isReadOnly: isDateOfBirthReadOnly,
            onBlur: handleFetchPrice,
            productName: productName
          }), /*#__PURE__*/_jsx(Media, {
            query: `${mediaQueries.small.only}`,
            children: isMobile => isMobile ? /*#__PURE__*/_jsx(ManulifeSwitchButton, {
              id: id,
              isDisabled: disabled,
              productName: productName
            }) : null
          })]
        }), /*#__PURE__*/_jsx(Media, {
          query: `${mediaQueries.small.only}`,
          children: isMobile => isMobile ? /*#__PURE__*/_jsx(ManulifeTravelerPrice, {
            error: isFetchPriceError,
            isLoading: isFetchingPrice,
            isSelected: isSelected,
            onRefetch: () => {
              setTravelerRefetchingPrice(true);
            },
            price: price
          }) : null
        })]
      }), /*#__PURE__*/_jsx(Media, {
        query: `${mediaQueries.medium.up}`,
        children: isDesktop => isDesktop ? /*#__PURE__*/_jsxs("div", {
          className: desktopPriceWrapperStyles,
          children: [/*#__PURE__*/_jsx(ManulifeSwitchButton, {
            id: id,
            isDisabled: disabled,
            productName: productName
          }), /*#__PURE__*/_jsx(ManulifeTravelerPrice, {
            className: travelerPriceDesktopStyles(price === null),
            error: isFetchPriceError,
            isLoading: isFetchingPrice,
            isSelected: isSelected,
            onRefetch: fetchPrice,
            price: price
          })]
        }) : null
      })]
    })
  }, name);
};
ManulifeTraveler.propTypes = {
  addTravelerPrice: PropTypes.func,
  addTravelerPriceError: PropTypes.func,
  ageType: PropTypes.string,
  dateFormat: PropTypes.string,
  dateOfBirth: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isActive: PropTypes.bool,
  isDateOfBirthReadOnly: PropTypes.bool,
  isFetchingPrice: PropTypes.bool,
  isRefetchingPrice: PropTypes.bool,
  isSelected: PropTypes.bool,
  isValidDateOfBirth: PropTypes.bool,
  name: PropTypes.string,
  onFetchPrice: PropTypes.func.isRequired,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  productName: PropTypes.string,
  province: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string
  }),
  selectTraveler: PropTypes.func.isRequired,
  setTravelerFetchingPrice: PropTypes.func.isRequired,
  setTravelerRefetchingPrice: PropTypes.func.isRequired
};
export default ManulifeTraveler;
//# sourceMappingURL=ManulifeTraveler.js.map